'use client';

import { Controller, FormProvider } from 'react-hook-form';
import { Typography, Button, Input, Icon } from '@/app/common';
import type { NewsletterBlock } from './newsletter.types';
import { processHeading } from '@workspace/lib/heading-utils';
import { cn, getBackgroundClass } from '@workspace/lib';
import { NEWSLETTER_MESSAGES } from '@workspace/constants/newsletter.constant';
import { useNewsletter } from './useNewsletter';
import { newsletterVariants, newsletterSuccessConfig } from '@workspace/blocks/Newsletter/newsletter.variants';

export function Newsletter({ block }: { block: NewsletterBlock }) {
  const {
    heading,
    description,
    placeholder = 'Enter your email address',
    buttonText = 'Subscribe',
    button,
    backgroundColor,
  } = block || ({} as NewsletterBlock);

  const { form, onSubmit, handleInvalidSubmit, showSuccess, loading } = useNewsletter();
  const { control, handleSubmit, formState } = form;
  const { errors } = formState;

  const backgroundClass = getBackgroundClass(backgroundColor);
  const headingText = processHeading(heading).text;

  const resolvedButtonText = button?.label || buttonText;
  const resolvedButtonVariant = (button?.style as any) || 'primary';

  const {
    section,
    wrapper,
    leftColumn,
    heading: headingSlot,
    description: descriptionSlot,
    rightColumn,
    inputGroup,
    label: labelSlot,
    input: inputSlot,
    inputText: inputTextSlot,
    errorWrapper,
    errorText,
    submitButton,
    submitButtonText,
    successWrapper,
    successHeading,
    successDescription,
    successIcon,
  } = newsletterVariants();

  const successContent = (
    <div className={cn('flex flex-col items-start justify-center', successWrapper())}>
      <div className="flex items-start gap-2">
        {newsletterSuccessConfig.showIcon && (
          <Icon
            name={(newsletterSuccessConfig.iconName || 'CircleCheck') as any}
            size={24}
            className={cn('mt-0.5 shrink-0', successIcon())}
          />
        )}
        <div className="flex flex-col">
          {newsletterSuccessConfig.heading && (
            <Typography variant="h3" className={successHeading()}>
              {newsletterSuccessConfig.heading}
            </Typography>
          )}
          <Typography variant="bodyMd" className={successDescription()}>
            {newsletterSuccessConfig.description}
          </Typography>
        </div>
      </div>
    </div>
  );

  return (
    <div className={cn(backgroundClass)}>
      <section className={cn('porta-container w-full', section())}>
        <div>
          {showSuccess && !newsletterSuccessConfig.replaceFormOnly ? (
            successContent
          ) : (
            <div className={cn('flex flex-col md:flex-row items-center', wrapper())}>
              {/* Left side */}
              <div className={cn('w-full', leftColumn())}>
                <Typography variant="h3" className={headingSlot()}>
                  {headingText}
                </Typography>
                <Typography variant="bodyMd" className={cn('max-w-prose', descriptionSlot())}>
                  {description}
                </Typography>
              </div>

              {/* Right side */}
              <div className={cn('w-full', rightColumn())}>
                {showSuccess && newsletterSuccessConfig.replaceFormOnly ? (
                  successContent
                ) : (
                  <FormProvider {...form}>
                    <form onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)} noValidate className="w-full">
                      <Controller
                        control={control}
                        name="honeypot"
                        render={({ field }) => (
                          <input type="text" tabIndex={-1} autoComplete="off" style={{ display: 'none' }} {...field} />
                        )}
                      />
                      <label htmlFor="newsletter-email" className={cn('block', labelSlot())}>
                        Email
                      </label>
                      <div className={cn('flex flex-col md:flex-row md:items-start', inputGroup())}>
                        <div className="flex-1 w-full">
                          <Controller
                            control={control}
                            name="email"
                            render={({ field }) => (
                              <Input
                                {...field}
                                placeholder={placeholder}
                                maxLength={255}
                                variant="field"
                                status={errors.email ? 'error' : 'default'}
                                className={cn(
                                  'w-full max-w-none',
                                  inputSlot(),
                                  errors.email && 'border-error! focus-within:border-error!',
                                )}
                                inputClassName={inputTextSlot?.()}
                              />
                            )}
                          />
                          {errors.email && (
                            <div className={cn(errorWrapper())}>
                              <Typography variant="bodyXs" className={cn(errorText())} role="alert">
                                {errors.email.message || NEWSLETTER_MESSAGES.ERROR}
                              </Typography>
                            </div>
                          )}
                        </div>

                        <Button
                          variant={resolvedButtonVariant}
                          type="submit"
                          isDisabled={loading}
                          aria-disabled={loading}
                          className={cn('w-full', submitButton())}
                        >
                          <span className={cn('block w-full text-center', submitButtonText())}>
                            {loading ? 'Sending...' : resolvedButtonText}
                          </span>
                        </Button>
                      </div>
                    </form>
                  </FormProvider>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
